import { hexToRgba } from '../core/color';
import { BUILTIN_GRADIENTS, gradientToLut } from '../core/gradient';
import type { AdjustmentId, AdjustmentState, RGBA } from '../core/types';
import { curvesToLut, linearCurves } from './curves';

// Re-exported so callers can keep importing the id from the adjustments module.
export type { AdjustmentId, AdjustmentState };

/** Shader `uType` values; keep in sync with the constants in `adjust.frag.glsl`. */
export const ADJUSTMENT_TYPES: Record<AdjustmentId, number> = {
  brightnessContrast: 0,
  hsl: 1,
  levels: 2,
  invert: 3,
  desaturate: 4,
  curves: 5,
  exposure: 6,
  vibrance: 7,
  colorBalance: 8,
  blackAndWhite: 9,
  photoFilter: 10,
  channelMixer: 11,
  posterize: 12,
  threshold: 13,
  gradientMap: 14,
  selectiveColor: 15,
  shadowsHighlights: 16,
};

/** Length of the `uParams` array in the shader. */
export const PARAM_SLOTS = 10;

export type Vec4 = readonly [number, number, number, number];

/** Non-slider editors an adjustment can ask the dialog for. */
export type AdjustmentExtra = 'curves' | 'gradient' | 'color';

interface ControlCommon {
  key: string;
  label: string;
  /**
   * Controls that belong to one choice of a `switch` control, keyed by that
   * choice's value. Selective Color and Channel Mixer show four sliders at a
   * time this way, exactly as Photoshop does.
   */
  group?: string;
}

export interface SliderControl extends ControlCommon {
  kind: 'slider';
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  suffix?: string;
}

export interface ToggleControl extends ControlCommon {
  kind: 'toggle';
  defaultValue: number;
}

export interface SwitchControl extends ControlCommon {
  kind: 'switch';
  options: { value: number; label: string; group?: string }[];
  defaultValue: number;
}

export type AdjustmentControl = SliderControl | ToggleControl | SwitchControl;

export interface AdjustmentDef {
  id: AdjustmentId;
  label: string;
  type: number;
  controls: AdjustmentControl[];
  extras?: readonly AdjustmentExtra[];
  /** Packs the state into the `uParams` slots the shader reads. */
  pack: (state: AdjustmentState) => Vec4[];
  /** 256-entry RGBA row for the adjustments that are a table lookup. */
  lut?: (state: AdjustmentState) => Uint8Array;
  /**
   * Radius, in pixels, of the blurred copy of the source the shader needs. Zero
   * means the pass reads only the pixel itself.
   */
  auxRadius?: (state: AdjustmentState) => number;
  /** Adjustments whose extras carry the whole edit are never a no-op. */
  alwaysApplies?: boolean;
  defaultColor?: RGBA;
}

const ZERO: Vec4 = [0, 0, 0, 0];

/** What a fresh Gradient Map starts from, as in Photoshop. */
export const BLACK_TO_WHITE = BUILTIN_GRADIENTS[0];

function slider(
  key: string,
  label: string,
  min: number,
  max: number,
  defaultValue: number,
  extra: Partial<SliderControl> = {},
): SliderControl {
  return { kind: 'slider', key, label, min, max, step: 1, defaultValue, ...extra };
}

function toggle(key: string, label: string, defaultValue = 0): ToggleControl {
  return { kind: 'toggle', key, label, defaultValue };
}

/** The nine ranges Selective Color works on, in Photoshop's order. */
export const SELECTIVE_TARGETS = [
  'reds',
  'yellows',
  'greens',
  'cyans',
  'blues',
  'magentas',
  'whites',
  'neutrals',
  'blacks',
] as const;

const SELECTIVE_COMPONENTS = [
  { key: 'cyan', label: 'Cyan' },
  { key: 'magenta', label: 'Magenta' },
  { key: 'yellow', label: 'Yellow' },
  { key: 'black', label: 'Black' },
] as const;

function selectiveControls(): AdjustmentControl[] {
  const controls: AdjustmentControl[] = [
    {
      kind: 'switch',
      key: 'target',
      label: 'Colors',
      defaultValue: 0,
      options: SELECTIVE_TARGETS.map((name, index) => ({
        value: index,
        label: name[0].toUpperCase() + name.slice(1),
        group: name,
      })),
    },
  ];
  for (const target of SELECTIVE_TARGETS) {
    for (const component of SELECTIVE_COMPONENTS) {
      controls.push(
        slider(`${target}.${component.key}`, component.label, -100, 100, 0, {
          group: target,
          suffix: '%',
        }),
      );
    }
  }
  controls.push(toggle('relative', 'Relative'));
  return controls;
}

const MIXER_ROWS = ['red', 'green', 'blue'] as const;

function mixerControls(): AdjustmentControl[] {
  const controls: AdjustmentControl[] = [
    {
      kind: 'switch',
      key: 'output',
      label: 'Output',
      defaultValue: 0,
      options: MIXER_ROWS.map((name, index) => ({
        value: index,
        label: name[0].toUpperCase() + name.slice(1),
        group: name,
      })),
    },
  ];
  for (const row of MIXER_ROWS) {
    for (const source of MIXER_ROWS) {
      controls.push(
        slider(`${row}.${source}`, source[0].toUpperCase() + source.slice(1), -200, 200, row === source ? 100 : 0, {
          group: row,
          suffix: '%',
        }),
      );
    }
    controls.push(
      slider(`${row}.constant`, 'Constant', -200, 200, 0, { group: row, suffix: '%' }),
    );
  }
  controls.push(toggle('monochrome', 'Monochrome'));
  return controls;
}

/** How far ±100 on a Color Balance slider can push a channel. */
const BALANCE_SCALE = 0.5;

export const ADJUSTMENTS: Record<AdjustmentId, AdjustmentDef> = {
  brightnessContrast: {
    id: 'brightnessContrast',
    label: 'Brightness/Contrast',
    type: ADJUSTMENT_TYPES.brightnessContrast,
    controls: [
      slider('brightness', 'Brightness', -100, 100, 0),
      slider('contrast', 'Contrast', -100, 100, 0),
    ],
    pack: ({ values: v }) => [[v.brightness / 100, v.contrast / 100, 0, 0]],
  },
  levels: {
    id: 'levels',
    label: 'Levels',
    type: ADJUSTMENT_TYPES.levels,
    controls: [
      slider('inBlack', 'Input black', 0, 254, 0),
      slider('inWhite', 'Input white', 1, 255, 255),
      slider('gamma', 'Gamma', 0.1, 9.99, 1, { step: 0.01 }),
      slider('outBlack', 'Output black', 0, 255, 0),
      slider('outWhite', 'Output white', 0, 255, 255),
    ],
    pack: ({ values: v }) => [
      [v.inBlack / 255, v.inWhite / 255, v.gamma, v.outBlack / 255],
      [v.outWhite / 255, 0, 0, 0],
    ],
  },
  curves: {
    id: 'curves',
    label: 'Curves',
    type: ADJUSTMENT_TYPES.curves,
    controls: [],
    extras: ['curves'],
    pack: () => [ZERO],
    lut: (state) => curvesToLut(state.curves ?? linearCurves()),
  },
  exposure: {
    id: 'exposure',
    label: 'Exposure',
    type: ADJUSTMENT_TYPES.exposure,
    controls: [
      slider('exposure', 'Exposure', -20, 20, 0, { step: 0.01, suffix: 'stops' }),
      slider('offset', 'Offset', -0.5, 0.5, 0, { step: 0.001 }),
      slider('gamma', 'Gamma', 0.1, 9.99, 1, { step: 0.01 }),
    ],
    pack: ({ values: v }) => [[v.exposure, v.offset, v.gamma, 0]],
  },
  vibrance: {
    id: 'vibrance',
    label: 'Vibrance',
    type: ADJUSTMENT_TYPES.vibrance,
    controls: [
      slider('vibrance', 'Vibrance', -100, 100, 0),
      slider('saturation', 'Saturation', -100, 100, 0),
    ],
    pack: ({ values: v }) => [[v.vibrance / 100, v.saturation / 100, 0, 0]],
  },
  hsl: {
    id: 'hsl',
    label: 'Hue/Saturation',
    type: ADJUSTMENT_TYPES.hsl,
    controls: [
      slider('hue', 'Hue', -180, 180, 0, { suffix: '°' }),
      slider('saturation', 'Saturation', -100, 100, 0),
      slider('lightness', 'Lightness', -100, 100, 0),
    ],
    pack: ({ values: v }) => [[v.hue / 360, v.saturation / 100, v.lightness / 100, 0]],
  },
  colorBalance: {
    id: 'colorBalance',
    label: 'Color Balance',
    type: ADJUSTMENT_TYPES.colorBalance,
    controls: [
      slider('shadowsRed', 'Shadows Cyan/Red', -100, 100, 0),
      slider('shadowsGreen', 'Shadows Magenta/Green', -100, 100, 0),
      slider('shadowsBlue', 'Shadows Yellow/Blue', -100, 100, 0),
      slider('midtonesRed', 'Midtones Cyan/Red', -100, 100, 0),
      slider('midtonesGreen', 'Midtones Magenta/Green', -100, 100, 0),
      slider('midtonesBlue', 'Midtones Yellow/Blue', -100, 100, 0),
      slider('highlightsRed', 'Highlights Cyan/Red', -100, 100, 0),
      slider('highlightsGreen', 'Highlights Magenta/Green', -100, 100, 0),
      slider('highlightsBlue', 'Highlights Yellow/Blue', -100, 100, 0),
      toggle('preserveLuminosity', 'Preserve Luminosity', 1),
    ],
    pack: ({ values: v }) => [
      [
        (v.shadowsRed / 100) * BALANCE_SCALE,
        (v.shadowsGreen / 100) * BALANCE_SCALE,
        (v.shadowsBlue / 100) * BALANCE_SCALE,
        0,
      ],
      [
        (v.midtonesRed / 100) * BALANCE_SCALE,
        (v.midtonesGreen / 100) * BALANCE_SCALE,
        (v.midtonesBlue / 100) * BALANCE_SCALE,
        0,
      ],
      [
        (v.highlightsRed / 100) * BALANCE_SCALE,
        (v.highlightsGreen / 100) * BALANCE_SCALE,
        (v.highlightsBlue / 100) * BALANCE_SCALE,
        0,
      ],
      [v.preserveLuminosity, 0, 0, 0],
    ],
  },
  blackAndWhite: {
    id: 'blackAndWhite',
    label: 'Black and White',
    type: ADJUSTMENT_TYPES.blackAndWhite,
    controls: [
      slider('reds', 'Reds', -200, 300, 40, { suffix: '%' }),
      slider('yellows', 'Yellows', -200, 300, 60, { suffix: '%' }),
      slider('greens', 'Greens', -200, 300, 40, { suffix: '%' }),
      slider('cyans', 'Cyans', -200, 300, 60, { suffix: '%' }),
      slider('blues', 'Blues', -200, 300, 20, { suffix: '%' }),
      slider('magentas', 'Magentas', -200, 300, 80, { suffix: '%' }),
      toggle('tint', 'Tint'),
    ],
    extras: ['color'],
    defaultColor: hexToRgba('#e9dcc0'),
    alwaysApplies: true,
    pack: ({ values: v, color }) => [
      [v.reds / 100, v.yellows / 100, v.greens / 100, 0],
      [v.cyans / 100, v.blues / 100, v.magentas / 100, 0],
      [color?.r ?? 1, color?.g ?? 1, color?.b ?? 1, v.tint],
    ],
  },
  photoFilter: {
    id: 'photoFilter',
    label: 'Photo Filter',
    type: ADJUSTMENT_TYPES.photoFilter,
    controls: [
      slider('density', 'Density', 0, 100, 25, { suffix: '%' }),
      toggle('preserveLuminosity', 'Preserve Luminosity', 1),
    ],
    extras: ['color'],
    defaultColor: hexToRgba('#ec8a00'),
    pack: ({ values: v, color }) => [
      [color?.r ?? 1, color?.g ?? 1, color?.b ?? 1, v.density / 100],
      [v.preserveLuminosity, 0, 0, 0],
    ],
  },
  channelMixer: {
    id: 'channelMixer',
    label: 'Channel Mixer',
    type: ADJUSTMENT_TYPES.channelMixer,
    controls: mixerControls(),
    pack: ({ values: v }) => [
      [v['red.red'] / 100, v['red.green'] / 100, v['red.blue'] / 100, v['red.constant'] / 100],
      [
        v['green.red'] / 100,
        v['green.green'] / 100,
        v['green.blue'] / 100,
        v['green.constant'] / 100,
      ],
      [v['blue.red'] / 100, v['blue.green'] / 100, v['blue.blue'] / 100, v['blue.constant'] / 100],
      [v.monochrome, 0, 0, 0],
    ],
  },
  invert: {
    id: 'invert',
    label: 'Invert',
    type: ADJUSTMENT_TYPES.invert,
    controls: [slider('amount', 'Amount', 0, 100, 100, { suffix: '%' })],
    pack: ({ values: v }) => [[v.amount / 100, 0, 0, 0]],
  },
  posterize: {
    id: 'posterize',
    label: 'Posterize',
    type: ADJUSTMENT_TYPES.posterize,
    controls: [slider('levels', 'Levels', 2, 255, 4)],
    pack: ({ values: v }) => [[v.levels, 0, 0, 0]],
  },
  threshold: {
    id: 'threshold',
    label: 'Threshold',
    type: ADJUSTMENT_TYPES.threshold,
    controls: [slider('level', 'Threshold Level', 1, 255, 128)],
    pack: ({ values: v }) => [[v.level / 255, 0, 0, 0]],
  },
  gradientMap: {
    id: 'gradientMap',
    label: 'Gradient Map',
    type: ADJUSTMENT_TYPES.gradientMap,
    controls: [toggle('reverse', 'Reverse')],
    extras: ['gradient'],
    alwaysApplies: true,
    pack: ({ values: v }) => [[v.reverse, 0, 0, 0]],
    lut: (state) => gradientToLut(state.gradient ?? BLACK_TO_WHITE),
  },
  selectiveColor: {
    id: 'selectiveColor',
    label: 'Selective Color',
    type: ADJUSTMENT_TYPES.selectiveColor,
    controls: selectiveControls(),
    pack: ({ values: v }) => {
      const slots: Vec4[] = SELECTIVE_TARGETS.map((target) => [
        v[`${target}.cyan`] / 100,
        v[`${target}.magenta`] / 100,
        v[`${target}.yellow`] / 100,
        v[`${target}.black`] / 100,
      ]);
      slots.push([v.relative, 0, 0, 0]);
      return slots;
    },
  },
  shadowsHighlights: {
    id: 'shadowsHighlights',
    label: 'Shadows/Highlights',
    type: ADJUSTMENT_TYPES.shadowsHighlights,
    controls: [
      slider('shadowsAmount', 'Shadows Amount', 0, 100, 35, { suffix: '%' }),
      slider('shadowsTone', 'Shadows Tone', 1, 100, 50, { suffix: '%' }),
      slider('highlightsAmount', 'Highlights Amount', 0, 100, 0, { suffix: '%' }),
      slider('highlightsTone', 'Highlights Tone', 1, 100, 50, { suffix: '%' }),
      slider('radius', 'Radius', 0, 250, 30, { suffix: 'px' }),
    ],
    pack: ({ values: v }) => [
      [
        v.shadowsAmount / 100,
        v.shadowsTone / 100,
        v.highlightsAmount / 100,
        v.highlightsTone / 100,
      ],
    ],
    auxRadius: ({ values: v }) => v.radius,
  },
  desaturate: {
    id: 'desaturate',
    label: 'Desaturate',
    type: ADJUSTMENT_TYPES.desaturate,
    controls: [slider('amount', 'Amount', 0, 100, 100, { suffix: '%' })],
    pack: ({ values: v }) => [[v.amount / 100, 0, 0, 0]],
  },
};

/** Image > Adjustments, in Photoshop's order. */
export const ADJUSTMENT_ORDER: AdjustmentId[] = [
  'brightnessContrast',
  'levels',
  'curves',
  'exposure',
  'vibrance',
  'hsl',
  'colorBalance',
  'blackAndWhite',
  'photoFilter',
  'channelMixer',
  'invert',
  'posterize',
  'threshold',
  'gradientMap',
  'selectiveColor',
  'shadowsHighlights',
  'desaturate',
];

export function defaultAdjustmentValues(id: AdjustmentId): Record<string, number> {
  const values: Record<string, number> = {};
  for (const control of ADJUSTMENTS[id].controls) values[control.key] = control.defaultValue;
  return values;
}

/** A fresh state for one adjustment, with the extras it declares filled in. */
export function defaultAdjustmentState(id: AdjustmentId): AdjustmentState {
  const definition = ADJUSTMENTS[id];
  const state: AdjustmentState = { id, values: defaultAdjustmentValues(id) };
  for (const extra of definition.extras ?? []) {
    if (extra === 'curves') state.curves = linearCurves();
    if (extra === 'gradient') state.gradient = BLACK_TO_WHITE;
    if (extra === 'color') state.color = definition.defaultColor ?? { r: 1, g: 1, b: 1, a: 1 };
  }
  return state;
}

/** True when applying the adjustment would not change a single pixel. */
export function isAdjustmentNeutral(state: AdjustmentState): boolean {
  const definition = ADJUSTMENTS[state.id];
  if (definition.alwaysApplies) return false;
  if (state.curves && !areCurvesUntouched(state)) return false;
  return definition.controls.every(
    (control) => state.values[control.key] === control.defaultValue,
  );
}

function areCurvesUntouched(state: AdjustmentState): boolean {
  const curves = state.curves;
  if (!curves) return true;
  const flat = curvesToLut(curves);
  for (let i = 0; i < 256; i += 1) {
    if (flat[i * 4] !== i || flat[i * 4 + 1] !== i || flat[i * 4 + 2] !== i) return false;
  }
  return true;
}

/** The uniform array the shader expects: every slot present, unused ones zero. */
export function packAdjustment(state: AdjustmentState): Float32Array {
  const slots = ADJUSTMENTS[state.id].pack(state);
  const data = new Float32Array(PARAM_SLOTS * 4);
  slots.slice(0, PARAM_SLOTS).forEach((slot, index) => data.set(slot, index * 4));
  return data;
}

/**
 * Identity of the lookup row for `state`, so a composited adjustment layer can
 * keep its texture between frames and only re-upload when the edit changes.
 */
export function adjustmentLutKey(state: AdjustmentState): string | null {
  if (!ADJUSTMENTS[state.id].lut) return null;
  return `${state.id}:${JSON.stringify(state.curves ?? null)}:${JSON.stringify(state.gradient ?? null)}`;
}
